import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import styles from "./style.module.scss";
import PrintPage from "@components/print/PrintElements/PrintPage";
import ReportPage, { ReportDataProps, ReportTitleProps } from "@components/dynamic/ReportPage";
import { useAppSelector } from "@/store/hooks";
import { ClientApi } from "@/api/ClientApi";
import { SalemanApi } from "@/api/SalemenApi";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { ReportTotalDetail } from "@utils/DocumentStatus";
import { ExcelHeading } from "@utils/ReportExcel";
import {
  ALL_CLIENTS_OPTION,
  ALL_SALEMEN_OPTION,
  buildSalesByClientRows,
  buildSalesByClientTitles,
  fetchSalesByClientProducts
} from "@utils/SalesByClientReport";

type SalesByClientReportPrintProps = {
  excelExport?: boolean
}

const ALL_CLIENTS_LABEL = "Todos los clientes";
const ALL_SALEMEN_LABEL = "Todos los vendedores";

const SalesByClientReportPrint = (props: SalesByClientReportPrintProps) => {
  const { excelExport = false } = props
  const params = useParams()
  const { clientId = "", salemanId = "", startDate = "", endDate = "", groupBy = "0", autoPrint = false } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTitles, setReportTitles] = useState<ReportTitleProps[]>([]);
  const [reportTotals, setReportTotals] = useState<ReportTotalDetail[]>([]);
  const [clientName, setClientName] = useState<string>(ALL_CLIENTS_LABEL);
  const [salemanName, setSalemanName] = useState<string>(ALL_SALEMEN_LABEL);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Se fija una sola vez: en cada render `moment()` daria una fecha distinta. */
  const [generatedAt] = useState<string>(() => moment().format());

  const groupByCategory = groupBy === "1";

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
      loadReportData()
    }
  }, [startDate, endDate, clientId, salemanId, groupBy]);

  const loadClientName = async (): Promise<string> => {
    if (!clientId || clientId === ALL_CLIENTS_OPTION) {
      return ALL_CLIENTS_LABEL;
    }

    try {
      const client = await ClientApi.get(clientId);
      return `${client?.nombre || ""}`;
    } catch (err) {
      console.log("Not able to load the client name", err)
      return "";
    }
  }

  const loadSalemanName = async (): Promise<string> => {
    if (!salemanId || salemanId === ALL_SALEMEN_OPTION) {
      return ALL_SALEMEN_LABEL;
    }

    try {
      const saleman = await SalemanApi.get(salemanId);
      return `${saleman?.nombre || ""}`;
    } catch (err) {
      console.log("Not able to load the saleman name", err)
      return "";
    }
  }

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      setReportTitles(buildSalesByClientTitles(groupByCategory))
      const [loadedClientName, loadedSalemanName] = await Promise.all([loadClientName(), loadSalemanName()])
      setClientName(loadedClientName)
      setSalemanName(loadedSalemanName)

      const result = await fetchSalesByClientProducts({
        clientId,
        salemanId,
        start: startDate,
        end: endDate,
        groupByCategory
      })

      setReportData(buildSalesByClientRows(result, groupByCategory, styles.totalRow))
      setReportTotals(result.rows.length > 0
        ? [{ title: "Total de ventas", total: result.totalAmount, currencyCode: "CRC" }]
        : [])
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const renderDateRange = (): string => {
    const start = moment(startDate).format("DD-MM-YYYY");
    const end = moment(endDate).format("DD-MM-YYYY");

    return `${start} - ${end}`;
  }

  const renderSubtitle = (): string => {
    const period = `${clientName} | ${salemanName} | ${renderDateRange()}`;

    return groupByCategory ? `${period} | Agrupado por categoria` : period;
  }

  const buildExcelHeading = (): ExcelHeading => ({
    title: "Reporte de ventas",
    details: [
      { label: "Empresa", value: `${suscription.nombre || ""}` },
      { label: "Cliente", value: clientName },
      { label: "Vendedor", value: salemanName },
      { label: "Rango de fechas", value: renderDateRange() },
      { label: "Agrupado por categoria", value: groupByCategory ? "Si" : "No" },
      { label: "Fecha de generacion", value: moment(generatedAt).format("DD-MM-YYYY HH:mm") },
    ]
  })

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={2000}
      >
        <ReportPage
          loading={isLoading}
          excelExport={excelExport}
          maxItemPerPage={44}
          suscription={suscription}
          reportConfig={{
            title: "Reporte de Ventas por Cliente",
            subtitle: renderSubtitle(),
            xmlFileName: "Reporte de Ventas por Cliente",
            date: generatedAt,
            excelHeading: buildExcelHeading(),
          }}
          reportData={reportData}
          reportTotals={reportTotals}
          reportTitles={reportTitles}
        />
      </PrintPage>
    </div>
  );
}

export default SalesByClientReportPrint;
